import moment from "moment";

export const DATE_FILTER_PRESET = {
    CHOOSE_DATE_RANGE: 1,
    YEAR_TO_DATE: 2,
    QUARTER_TO_DATE: 3,
    MONTH_TO_DATE: 4,
    LAST_YEAR: 5,
    LAST_QUARTER: 6,
    LAST_MONTH: 7,
    NEXT_YEAR: 8,
    NEXT_QUARTER: 9,
    NEXT_MONTH: 10,
    UPCOMING_90_DAYS: 11,
    UPCOMING_6_MONTHS: 12,
    UPCOMING_12_MONTHS: 13,
    UPCOMING_18_MONTHS: 14,
    UPCOMING_24_MONTHS: 15,
};

export const DATE_FILTER_OPTIONS = [
    { value: DATE_FILTER_PRESET.CHOOSE_DATE_RANGE, label: "Choose date range" },
    { value: DATE_FILTER_PRESET.YEAR_TO_DATE, label: "Year-to-date" },
    { value: DATE_FILTER_PRESET.QUARTER_TO_DATE, label: "Quarter-to-date" },
    { value: DATE_FILTER_PRESET.MONTH_TO_DATE, label: "Month-to-date" },
    { value: DATE_FILTER_PRESET.LAST_YEAR, label: "Last year" },
    { value: DATE_FILTER_PRESET.LAST_QUARTER, label: "Last quarter" },
    { value: DATE_FILTER_PRESET.LAST_MONTH, label: "Last month" },
    { value: DATE_FILTER_PRESET.NEXT_YEAR, label: "Next year" },
    { value: DATE_FILTER_PRESET.NEXT_QUARTER, label: "Next quarter" },
    { value: DATE_FILTER_PRESET.NEXT_MONTH, label: "Next month" },
    { value: DATE_FILTER_PRESET.UPCOMING_90_DAYS, label: "Upcoming 90 days" },
    { value: DATE_FILTER_PRESET.UPCOMING_6_MONTHS, label: "Upcoming 6 months" },
    { value: DATE_FILTER_PRESET.UPCOMING_12_MONTHS, label: "Upcoming 12 months" },
    { value: DATE_FILTER_PRESET.UPCOMING_18_MONTHS, label: "Upcoming 18 months" },
    { value: DATE_FILTER_PRESET.UPCOMING_24_MONTHS, label: "Upcoming 24 months" },
];

// Resolves a preset key to { start, end } in YYYY-MM-DD format.
// Returns null for CHOOSE_DATE_RANGE (caller supplies dates manually).
export const resolvePresetDates = (preset) => {
    const today = moment();
    const fmt = (m) => m.format("YYYY-MM-DD");

    switch (preset) {
        case DATE_FILTER_PRESET.YEAR_TO_DATE:
            return { start: fmt(today.clone().startOf("year")), end: fmt(today) };

        case DATE_FILTER_PRESET.QUARTER_TO_DATE:
            return { start: fmt(today.clone().startOf("quarter")), end: fmt(today) };

        case DATE_FILTER_PRESET.MONTH_TO_DATE:
            return { start: fmt(today.clone().startOf("month")), end: fmt(today) };

        case DATE_FILTER_PRESET.LAST_YEAR:
            return {
                start: fmt(today.clone().subtract(1, "year").startOf("year")),
                end: fmt(today.clone().subtract(1, "year").endOf("year")),
            };

        case DATE_FILTER_PRESET.LAST_QUARTER:
            return {
                start: fmt(today.clone().subtract(1, "quarter").startOf("quarter")),
                end: fmt(today.clone().subtract(1, "quarter").endOf("quarter")),
            };

        case DATE_FILTER_PRESET.LAST_MONTH:
            return {
                start: fmt(today.clone().subtract(1, "month").startOf("month")),
                end: fmt(today.clone().subtract(1, "month").endOf("month")),
            };

        case DATE_FILTER_PRESET.NEXT_YEAR:
            return {
                start: fmt(today.clone().add(1, "year").startOf("year")),
                end: fmt(today.clone().add(1, "year").endOf("year")),
            };

        case DATE_FILTER_PRESET.NEXT_QUARTER:
            return {
                start: fmt(today.clone().add(1, "quarter").startOf("quarter")),
                end: fmt(today.clone().add(1, "quarter").endOf("quarter")),
            };

        case DATE_FILTER_PRESET.NEXT_MONTH:
            return {
                start: fmt(today.clone().add(1, "month").startOf("month")),
                end: fmt(today.clone().add(1, "month").endOf("month")),
            };

        case DATE_FILTER_PRESET.UPCOMING_90_DAYS:
            return { start: fmt(today), end: fmt(today.clone().add(90, "days")) };

        case DATE_FILTER_PRESET.UPCOMING_6_MONTHS:
            return { start: fmt(today), end: fmt(today.clone().add(6, "months")) };

        case DATE_FILTER_PRESET.UPCOMING_12_MONTHS:
            return { start: fmt(today), end: fmt(today.clone().add(12, "months")) };

        case DATE_FILTER_PRESET.UPCOMING_18_MONTHS:
            return { start: fmt(today), end: fmt(today.clone().add(18, "months")) };

        case DATE_FILTER_PRESET.UPCOMING_24_MONTHS:
            return { start: fmt(today), end: fmt(today.clone().add(24, "months")) };

        default:
            return null;
    }
};

// Notice date (Lease Events) keeps its own legacy preset values (plain month counts)
// so existing saved/sticky defaults stay numerically compatible; only "Choose date range"
// shares the same sentinel value as DATE_FILTER_PRESET.CHOOSE_DATE_RANGE.
export const NOTICE_DATE_FILTER_PRESET = {
    CHOOSE_DATE_RANGE: DATE_FILTER_PRESET.CHOOSE_DATE_RANGE,
    ALL: 0,
    WITHIN_3_MONTHS: 3,
    WITHIN_6_MONTHS: 6,
    WITHIN_12_MONTHS: 12,
    WITHIN_18_MONTHS: 18,
    WITHIN_24_MONTHS: 24,
    WITHIN_36_MONTHS: 36,
};

export const NOTICE_DATE_FILTER_OPTIONS = [
    { value: NOTICE_DATE_FILTER_PRESET.CHOOSE_DATE_RANGE, label: "Choose date range" },
    { value: NOTICE_DATE_FILTER_PRESET.ALL, label: "All" },
    { value: NOTICE_DATE_FILTER_PRESET.WITHIN_3_MONTHS, label: "Within 3 months" },
    { value: NOTICE_DATE_FILTER_PRESET.WITHIN_6_MONTHS, label: "Within 6 months" },
    { value: NOTICE_DATE_FILTER_PRESET.WITHIN_12_MONTHS, label: "Within 12 months" },
    { value: NOTICE_DATE_FILTER_PRESET.WITHIN_18_MONTHS, label: "Within 18 months" },
    { value: NOTICE_DATE_FILTER_PRESET.WITHIN_24_MONTHS, label: "Within 24 months" },
    { value: NOTICE_DATE_FILTER_PRESET.WITHIN_36_MONTHS, label: "Within 36 months" },
];

// Mirrors the legacy computeNoticeDates() logic: "All" clears the range, every other
// preset is [today, today + N months].
export const resolveNoticeDatePresetDates = (preset) => {
    const today = moment.utc();
    const fmt = (m) => m.format("YYYY-MM-DD");

    if (preset === NOTICE_DATE_FILTER_PRESET.ALL) {
        return { start: "", end: "" };
    }

    switch (preset) {
        case NOTICE_DATE_FILTER_PRESET.WITHIN_3_MONTHS:
        case NOTICE_DATE_FILTER_PRESET.WITHIN_6_MONTHS:
        case NOTICE_DATE_FILTER_PRESET.WITHIN_12_MONTHS:
        case NOTICE_DATE_FILTER_PRESET.WITHIN_18_MONTHS:
        case NOTICE_DATE_FILTER_PRESET.WITHIN_24_MONTHS:
        case NOTICE_DATE_FILTER_PRESET.WITHIN_36_MONTHS:
            return { start: fmt(today), end: fmt(today.clone().add(preset, "months")) };

        default:
            return { start: "", end: "" };
    }
};

// Last comment date (Transaction, Pre-Transaction, Lease Event) uses its own trailing
// day-count presets instead of the generic to-date/upcoming list. Values start at 101 to
// stay clear of DATE_FILTER_PRESET's range (including its CHOOSE_DATE_RANGE sentinel).
export const LAST_COMMENT_DATE_FILTER_PRESET = {
    TODAY: 101,
    LAST_7_DAYS: 102,
    LAST_14_DAYS: 103,
    LAST_30_DAYS: 104,
    LAST_60_DAYS: 105,
    LAST_90_DAYS: 106,
};

export const LAST_COMMENT_DATE_FILTER_OPTIONS = [
    { value: LAST_COMMENT_DATE_FILTER_PRESET.TODAY, label: "Today" },
    { value: LAST_COMMENT_DATE_FILTER_PRESET.LAST_7_DAYS, label: "Last 7 days" },
    { value: LAST_COMMENT_DATE_FILTER_PRESET.LAST_14_DAYS, label: "Last 14 days" },
    { value: LAST_COMMENT_DATE_FILTER_PRESET.LAST_30_DAYS, label: "Last 30 days" },
    { value: LAST_COMMENT_DATE_FILTER_PRESET.LAST_60_DAYS, label: "Last 60 days" },
    { value: LAST_COMMENT_DATE_FILTER_PRESET.LAST_90_DAYS, label: "Last 90 days" },
];

export const resolveLastCommentDatePresetDates = (preset) => {
    const today = moment();
    const fmt = (m) => m.format("YYYY-MM-DD");

    switch (preset) {
        case LAST_COMMENT_DATE_FILTER_PRESET.TODAY:
            return { start: fmt(today), end: fmt(today) };

        case LAST_COMMENT_DATE_FILTER_PRESET.LAST_7_DAYS:
            return { start: fmt(today.clone().subtract(7, "days")), end: fmt(today) };

        case LAST_COMMENT_DATE_FILTER_PRESET.LAST_14_DAYS:
            return { start: fmt(today.clone().subtract(14, "days")), end: fmt(today) };

        case LAST_COMMENT_DATE_FILTER_PRESET.LAST_30_DAYS:
            return { start: fmt(today.clone().subtract(30, "days")), end: fmt(today) };

        case LAST_COMMENT_DATE_FILTER_PRESET.LAST_60_DAYS:
            return { start: fmt(today.clone().subtract(60, "days")), end: fmt(today) };

        case LAST_COMMENT_DATE_FILTER_PRESET.LAST_90_DAYS:
            return { start: fmt(today.clone().subtract(90, "days")), end: fmt(today) };

        default:
            return null;
    }
};
